import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import DashboardShell from "./dashboards/DashboardShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import MapView from "../components/MapView";
import ImageUploader from "../components/ImageUploader";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, MapPin } from "lucide-react";

const cats = [
  { v: "marche", l: "Marché" }, { v: "foire", l: "Foire" }, { v: "salon", l: "Salon" },
  { v: "festival", l: "Festival" }, { v: "exposition", l: "Exposition" }, { v: "autre", l: "Autre" },
];

const services = [
  "electricite", "wifi", "tables", "chaises", "parking_exposants", "gardiennage",
  "sanitaires", "restauration", "acces_camion", "stockage", "nettoyage", "securite",
  "badge_exposant", "montage", "demontage", "sonorisation", "eclairage",
];

const sampleImages = [
  "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200&q=85",
  "https://images.unsplash.com/photo-1772903191730-fa9bc478c1de?w=1200&q=85",
  "https://images.pexels.com/photos/6950031/pexels-photo-6950031.jpeg?auto=compress&w=1200",
  "https://images.unsplash.com/photo-1775866914966-5bd2194c464f?w=1200&q=85",
];

export default function CreateEvent() {
  const nav = useNavigate();
  const [saving, setSaving] = useState(false);
  const [event, setEvent] = useState({
    title: "", description: "", category: "marche", city: "", address: "",
    start_date: "", end_date: "",
    images: [], services: [],
  });
  const [coords, setCoords] = useState({ lat: null, lng: null });

  // Auto-geocode when city/address change (debounced)
  useEffect(() => {
    if (!event.city) { setCoords({ lat: null, lng: null }); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/geocode", {
          params: { city: event.city, address: event.address },
        });
        setCoords({ lat: data.lat, lng: data.lng });
      } catch {}
    }, 600);
    return () => clearTimeout(t);
  }, [event.city, event.address]);
  const [booths, setBooths] = useState([
    { name: "Stand standard", price: 180, size: "6m²", quantity: 10, options: [] }
  ]);

  const toggleService = (s) => {
    setEvent((e) => ({
      ...e,
      services: e.services.includes(s) ? e.services.filter(x => x !== s) : [...e.services, s],
    }));
  };

  const addBooth = () => setBooths([...booths, { name: "", price: 100, size: "", quantity: 1, options: [] }]);
  const removeBooth = (i) => setBooths(booths.filter((_, j) => j !== i));
  const updateBooth = (i, k, v) => setBooths(booths.map((b, j) => j === i ? { ...b, [k]: v } : b));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const images = event.images?.length ? event.images : [sampleImages[0]];
      const { data: ev } = await api.post("/events", {
        ...event,
        images,
        start_date: new Date(event.start_date).toISOString(),
        end_date: new Date(event.end_date).toISOString(),
      });
      for (const b of booths) {
        if (!b.name) continue;
        await api.post("/booths", {
          event_id: ev.id, name: b.name, description: "",
          price: parseFloat(b.price), size: b.size, quantity: parseInt(b.quantity),
          options: b.options,
        });
      }
      toast.success("Événement publié");
      nav(`/events/${ev.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardShell title="Nouvel événement" subtitle="Publiez votre événement en quelques minutes">
      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="create-event-form">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white border border-ink/10 rounded-md p-6 space-y-4">
            <h2 className="font-heading font-medium text-lg text-ink">Informations</h2>
            <div><Label>Titre</Label><Input data-testid="event-title" required value={event.title} onChange={(e) => setEvent({...event, title: e.target.value})} /></div>
            <div><Label>Description</Label><Textarea data-testid="event-description" required value={event.description} onChange={(e) => setEvent({...event, description: e.target.value})} rows={4} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Catégorie</Label>
                <Select value={event.category} onValueChange={(v) => setEvent({...event, category: v})}>
                  <SelectTrigger data-testid="event-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{cats.map(c => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Ville</Label><Input data-testid="event-city" required value={event.city} onChange={(e) => setEvent({...event, city: e.target.value})} /></div>
            </div>
            <div><Label>Adresse</Label><Input required value={event.address} onChange={(e) => setEvent({...event, address: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date de début</Label><Input data-testid="event-start" type="datetime-local" required value={event.start_date} onChange={(e) => setEvent({...event, start_date: e.target.value})} /></div>
              <div><Label>Date de fin</Label><Input data-testid="event-end" type="datetime-local" required value={event.end_date} onChange={(e) => setEvent({...event, end_date: e.target.value})} /></div>
            </div>
            <div>
              <Label>Photos</Label>
              <ImageUploader
                value={event.images}
                onChange={(images) => setEvent({ ...event, images })}
                max={5}
                presets={sampleImages}
              />
            </div>
          </div>

          <div className="bg-white border border-ink/10 rounded-md p-6">
            <h2 className="font-heading font-medium text-lg text-ink mb-4">Services disponibles</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {services.map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={event.services.includes(s)} onCheckedChange={() => toggleService(s)} data-testid={`service-${s}`} />
                  <span className="capitalize">{s.replace(/_/g, " ")}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="bg-white border border-ink/10 rounded-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading font-medium text-lg text-ink">Stands</h2>
              <Button type="button" variant="outline" size="sm" onClick={addBooth} data-testid="add-booth"><Plus className="h-4 w-4 mr-1" /> Ajouter</Button>
            </div>
            <div className="space-y-3">
              {booths.map((b, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end p-3 bg-cream-50 rounded-md">
                  <div className="col-span-12 md:col-span-4"><Label className="text-xs">Nom</Label><Input value={b.name} onChange={(e) => updateBooth(i, "name", e.target.value)} /></div>
                  <div className="col-span-4 md:col-span-2"><Label className="text-xs">Taille</Label><Input value={b.size} onChange={(e) => updateBooth(i, "size", e.target.value)} /></div>
                  <div className="col-span-4 md:col-span-2"><Label className="text-xs">Prix</Label><Input type="number" value={b.price} onChange={(e) => updateBooth(i, "price", e.target.value)} /></div>
                  <div className="col-span-4 md:col-span-2"><Label className="text-xs">Quantité</Label><Input type="number" value={b.quantity} onChange={(e) => updateBooth(i, "quantity", e.target.value)} /></div>
                  <div className="col-span-12 md:col-span-2">
                    <Button type="button" variant="ghost" onClick={() => removeBooth(i)} className="w-full"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside className="self-start lg:sticky lg:top-24 space-y-4">
          {coords.lat ? (
            <div className="bg-white border border-ink/10 rounded-md overflow-hidden">
              <div className="p-3 border-b border-ink/10">
                <p className="text-label text-muted-foreground mb-1">Aperçu sur la carte</p>
                <p className="text-xs text-trust flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> Localisé en France
                </p>
              </div>
              <div style={{ height: 220 }}>
                <MapView
                  items={[{ id: "preview", lat: coords.lat, lng: coords.lng, title: event.title || event.city, city: event.city }]}
                  type="event"
                />
              </div>
            </div>
          ) : event.city ? (
            <div className="bg-cream-200 border border-accent/30 rounded-md p-4 text-xs text-accent">
              <MapPin className="h-3 w-3 inline mr-1" />
              Adresse non localisée. L'événement sera publié mais n'apparaîtra pas sur la carte tant que l'adresse n'est pas reconnue.
            </div>
          ) : null}

          <div className="bg-white border border-ink/10 rounded-md p-6">
            <h3 className="font-heading font-medium text-lg text-ink mb-2">Publication</h3>
            <p className="text-sm text-muted-foreground mb-6">Votre événement sera visible immédiatement. Vous pourrez le modifier à tout moment.</p>
            <Button data-testid="event-submit" type="submit" disabled={saving} className="w-full bg-ink hover:bg-ink/85 text-cream rounded-none">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publier l'événement"}
            </Button>
          </div>
        </aside>
      </form>
    </DashboardShell>
  );
}
